import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectMilestones() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', due_date: '', planned_start_date: '', weight: '', required_for_completion: false, approval_required: false });
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newComment, setNewComment] = useState('');

  const load = useCallback(() => {
    if (!companyId || !projectId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/projects/${projectId}/milestones`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setMilestones(Array.isArray(d) ? d : []))
      .catch(() => setMilestones([])).finally(() => setLoading(false));
  }, [companyId, projectId, API]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!companyId || !projectId) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/team?active_only=1`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setTeamMembers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, projectId, API]);

  const loadDetail = (id) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/milestones/${id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => { setDetail(d); setShowDetail(true); }).catch(() => {});
  };

  const handleSave = () => {
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${API}/companies/${companyId}/projects/${projectId}/milestones/${editId}` : `${API}/companies/${companyId}/projects/${projectId}/milestones`;
    const body = { ...form };
    if (!body.weight) delete body.weight;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowModal(false); setEditId(null); resetForm(); load(); })
      .catch(() => window.alert('Failed to save milestone'));
  };

  const resetForm = () => setForm({ name: '', description: '', due_date: '', planned_start_date: '', weight: '', required_for_completion: false, approval_required: false });

  const changeStatus = (id, status) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/milestones/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status })
    }).then(r => r.ok ? r.json() : null).then(() => { load(); if (detail?.id === id) loadDetail(id); }).catch(() => {});
  };

  const addComment = (id) => {
    if (!newComment.trim()) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/milestones/${id}/comments`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ comment: newComment })
    }).then(r => r.ok ? r.json() : null).then(() => { setNewComment(''); loadDetail(id); }).catch(() => {});
  };

  const statusColor = { not_started: '#94a3b8', in_progress: '#3b82f6', blocked: '#ef4444', in_review: '#f59e0b', completed: '#22c55e', cancelled: '#6b7280' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <button onClick={() => navigate(`/company/projects/${projectId}`)} style={linkBtn}>← Back to Project</button>
          <h2 style={{ margin: '4px 0 0', fontSize: '1.4rem', fontWeight: 700 }}>🏁 Milestones</h2>
        </div>
        <button onClick={() => { setEditId(null); resetForm(); setShowModal(true); }} style={primaryBtn}>+ New Milestone</button>
      </div>

      {initialLoading ? <p>Loading...</p> : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {milestones.map(m => {
            const isOverdue = m.due_date && new Date(m.due_date) < new Date() && !['completed','cancelled'].includes(m.status);
            return (
              <div key={m.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => loadDetail(m.id)}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 600 }}>{m.name}</span>
                    <span style={{ ...badge, backgroundColor: statusColor[m.status] || '#6b7280' }}>{(m.status || '').replace(/_/g,' ')}</span>
                    {m.required_for_completion && <span style={{ fontSize: '0.65rem', color: '#ef4444', fontWeight: 600 }}>REQUIRED</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '16px' }}>
                    <span>Owner: {m.owner?.name || '-'}</span>
                    <span style={{ color: isOverdue ? '#ef4444' : '#64748b' }}>Due: {m.due_date || 'Not set'}</span>
                    <span>Tasks: {m.completed_tasks_count || 0}/{m.total_tasks_count || 0}</span>
                    <span>Deliverables: {m.completed_deliverables_count || 0}/{m.total_deliverables_count || 0}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#3b82f6' }}>{m.progress_percentage || 0}%</div>
                  </div>
                  {!['completed','cancelled'].includes(m.status) && (
                    <button onClick={(e) => { e.stopPropagation(); changeStatus(m.id, 'completed'); }} style={smallBtn} title="Complete">✓</button>
                  )}
                </div>
              </div>
            );
          })}
          {milestones.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center' }}>No milestones yet</p>}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{editId ? 'Edit Milestone' : 'New Milestone'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input placeholder="Milestone Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>Start</label><input type="date" value={form.planned_start_date} onChange={e => setForm({ ...form, planned_start_date: e.target.value })} style={inputStyle} /></div>
                <div style={{ flex: 1 }}><label style={labelStyle}>Due</label><input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} style={inputStyle} /></div>
              </div>
              <input type="number" placeholder="Weight (%)" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} style={inputStyle} />
              <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, minHeight: '50px' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={form.required_for_completion} onChange={e => setForm({ ...form, required_for_completion: e.target.checked })} /> Required for Project Completion
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={form.approval_required} onChange={e => setForm({ ...form, approval_required: e.target.checked })} /> Approval Required
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              <button onClick={handleSave} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Panel */}
      {showDetail && detail && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '650px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px' }}>{detail.name}</h3>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{detail.reference}</span>
              </div>
              <button onClick={() => setShowDetail(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <span style={{ ...badge, backgroundColor: statusColor[detail.status] || '#6b7280' }}>{(detail.status || '').replace(/_/g,' ')}</span>
              <span style={{ fontSize: '0.8rem', color: '#3b82f6', fontWeight: 700 }}>{detail.progress_percentage || 0}% Progress</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px', fontSize: '0.82rem' }}>
              <div><strong>Owner:</strong> {detail.owner?.name || '-'}</div>
              <div><strong>Due:</strong> {detail.due_date || '-'}</div>
              <div><strong>Weight:</strong> {detail.weight || '-'}%</div>
              <div><strong>Required:</strong> {detail.required_for_completion ? 'Yes' : 'No'}</div>
            </div>

            {/* Linked Tasks */}
            {Array.isArray(detail.tasks) && detail.tasks.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Linked Tasks ({detail.tasks.length})</h4>
                {detail.tasks.map(t => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                    <span>{t.name} <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>({t.assigned_user?.name || '-'})</span></span>
                    <span style={{ ...badge, backgroundColor: statusColor[t.status] || '#6b7280', fontSize: '0.65rem' }}>{t.status}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Linked Deliverables */}
            {Array.isArray(detail.deliverables) && detail.deliverables.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Linked Deliverables ({detail.deliverables.length})</h4>
                {detail.deliverables.map(d => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                    <span>{d.name}</span>
                    <span style={{ ...badge, backgroundColor: statusColor[d.status] || '#6b7280', fontSize: '0.65rem' }}>{d.status}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Comments */}
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Comments</h4>
              {Array.isArray(detail.comments) && detail.comments.map(c => (
                <div key={c.id} style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                  <strong>{c.user?.name || 'Unknown'}</strong> <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{new Date(c.created_at).toLocaleString()}</span>
                  <div>{c.comment}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                <input placeholder="Add comment..." value={newComment} onChange={e => setNewComment(e.target.value)} style={{ ...inputStyle, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addComment(detail.id)} />
                <button onClick={() => addComment(detail.id)} style={smallBtn}>Post</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const badge = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' };
const primaryBtn = { padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const cancelBtn = { padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' };
const smallBtn = { padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' };
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85rem', padding: 0 };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' };
